import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { jobsEndpoint } from "../constants";

const OfferDetails = () => {
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();
  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchOfferDetails = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${jobsEndpoint}${id}/offer_detail/`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`,
          }
        });
        setOffer(response.data);
      } catch (err) {
        setError("Failed to load offer details.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchOfferDetails();
    }
  }, [id, token]);

  const handleOfferAccept = async () => {
    try {
      setActionLoading(true);
      await axios.post(
        `${jobsEndpoint}${id}/offer_accept/`,
        {},
        {
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json',
          }
        }
      );
      
      setOffer(prev => ({ ...prev, status: "ACCEPTED" }));
    } catch (err) {
      console.error("Failed to update offer:", err);
      setError("Failed to update offer status.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleOfferDecline = async () => {
    try {
      setActionLoading(true);
      await axios.post(`${jobsEndpoint}${id}/offer_decline/`, {}, {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        }
      });
      
      setOffer(prev => ({ ...prev, status: "DECLINED" }));
    } catch (err) {
      console.error("Failed to update offer:", err);
      setError("Failed to update offer status.");
    } finally {
      setActionLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      draft: 'bg-blue-100 text-blue-600 border-blue-400',
      pending: 'bg-yellow-100 text-yellow-600 border-yellow-400',
      accepted: 'bg-green-100 text-green-600 border-green-400',
      declined: 'bg-red-100 text-red-600 border-red-400',
      rejected: 'bg-red-100 text-red-600 border-red-400',
      withdrawn: 'bg-gray-100 text-gray-600 border-gray-400',
      expired: 'bg-orange-100 text-orange-600 border-orange-400'
    };
    return statusClasses[status?.toLowerCase()] || 'bg-gray-100 text-gray-600 border-gray-400';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatContractType = (type) => {
    const contractTypes = {
      'PERMANENT': 'Permanent',
      'CONTRACT': 'Fixed-Term Contract',
      'TEMPORARY': 'Temporary',
      'FREELANCE': 'Freelance',
      'INTERNSHIP': 'Internship'
    };
    return contractTypes[type] || type;
  };

  const formatLocationType = (type) => {
    const locationTypes = {
      'OFFICE': 'Office',
      'REMOTE': 'Remote',
      'HYBRID': 'Hybrid'
    };
    return locationTypes[type] || type;
  };

  const isOfferExpired = () => {
    if (!offer?.expire_date) return false;
    return new Date(offer.expire_date) < new Date();
  };

  const canTakeAction = () => {
    return offer?.status === 'DRAFT' && !isOfferExpired();
  };

  const getRecipientName = () => {
    if (offer?.recipient?.first_name) {
      return offer.recipient.first_name;
    }
    return 'there'; // fallback greeting
  };

  const getSenderName = () => {
    if (offer?.sender?.first_name && offer?.sender?.last_name) {
      return `${offer.sender.first_name} ${offer.sender.last_name}`;
    }
    return offer?.sender?.email || 'Hiring Team';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your offer...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-6">
          <div className="text-center">
            <div className="text-red-500 text-5xl mb-4">⚠️</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to Load Offer</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => navigate("/offers")}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Back to Offers
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!offer) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-6">
          <div className="text-center">
            <div className="text-gray-400 text-5xl mb-4">📄</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Offer Not Found</h2>
            <p className="text-gray-600 mb-4">The offer you're looking for doesn't exist or has been removed.</p>
            <button
              onClick={() => navigate("/offers")}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Back to Offers
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate("/offers")}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <span className="mr-2">←</span>
              Back to Offers
            </button>
            <div className="flex items-center gap-3">
              <span className={`text-sm border py-1 px-3 rounded-full capitalize font-medium ${getStatusBadge(offer.status)}`}>
                {offer.status || 'Draft'}
              </span>
              {isOfferExpired() && (
                <span className="text-sm border py-1 px-3 rounded-full bg-red-100 text-red-600 border-red-400">
                  Expired
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Offer Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Offer Letter Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-6 text-white">
            <h1 className="text-2xl font-bold mb-2">
              Job Offer for {offer.job?.title || offer.service?.title || 'Position'} at {offer.job?.company || offer.service?.company || 'Company'}
            </h1>
            <p className="text-blue-100">
              Congratulations! We're excited to extend this offer to you.
            </p>
          </div>

          {/* Offer Letter Content */}
          <div className="px-8 py-6">
            {/* Greeting */}
            <div className="mb-6">
              <p className="text-lg text-gray-800 mb-4">
                <strong>Hi {getRecipientName()},</strong>
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                I'm delighted to share that <strong>{offer.job?.company || offer.service?.company || 'our company'}</strong> would like to offer you the position of <strong>{offer.job?.title || offer.service?.title || 'the role'}</strong> following your recent interactions with us!
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                We were genuinely impressed with your background, enthusiasm, and potential to thrive in our team. Below are the details of your offer:
              </p>
            </div>

            {/* Offer Summary */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-blue-100 pb-2">
                Offer Summary
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex">
                    <span className="text-gray-600 w-32">• Position:</span>
                    <span className="font-medium text-gray-900">{offer.job?.title || offer.service?.title || 'Not specified'}</span>
                  </div>
                  <div className="flex">
                    <span className="text-gray-600 w-32">• Department:</span>
                    <span className="font-medium text-gray-900">{offer.job?.department || 'Not specified'}</span>
                  </div>
                  <div className="flex">
                    <span className="text-gray-600 w-32">• Contract Type:</span>
                    <span className="font-medium text-gray-900">{formatContractType(offer.contract_type)}</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex">
                    <span className="text-gray-600 w-32">• Location:</span>
                    <span className="font-medium text-gray-900">
                      {formatLocationType(offer.location_type)}
                      {offer.job?.location && ` - ${offer.job.location}`}
                    </span>
                  </div>
                  <div className="flex">
                    <span className="text-gray-600 w-32">• Start Date:</span>
                    <span className="font-medium text-gray-900">To be discussed</span>
                  </div>
                  {offer.expire_date && (
                    <div className="flex">
                      <span className="text-gray-600 w-32">• Offer Expires:</span>
                      <span className={`font-medium ${isOfferExpired() ? 'text-red-600' : 'text-gray-900'}`}>
                        {formatDate(offer.expire_date)}
                        {isOfferExpired() && ' (Expired)'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Compensation & Benefits */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-blue-100 pb-2">
                Compensation & Benefits
              </h2>
              <div className="space-y-3">
                <div className="flex items-center">
                  <span className="text-gray-600 w-40">• Base Salary:</span>
                  <span className="font-bold text-2xl text-green-600">
                    {offer.income ? formatCurrency(offer.income) : 'To be discussed'} per annum
                  </span>
                </div>
                {offer.bonus && (
                  <div className="flex">
                    <span className="text-gray-600 w-40">• Bonus/Commission:</span>
                    <span className="font-medium text-gray-900">{offer.bonus}</span>
                  </div>
                )}
                <div className="flex">
                  <span className="text-gray-600 w-40">• Benefits Include:</span>
                  <span className="font-medium text-gray-900">
                    Details to be provided with full contract
                  </span>
                </div>
              </div>
            </div>

            {/* Additional Notes */}
            {offer.notes && (
              <div className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-blue-100 pb-2">
                  Additional Information
                </h2>
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-gray-700 whitespace-pre-wrap">{offer.notes}</p>
                </div>
              </div>
            )}

            {/* Next Steps */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-blue-100 pb-2">
                Next Steps
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We'd love to hear your thoughts{offer.expire_date && ` by ${formatDate(offer.expire_date)}`}. Once you're happy with everything, we'll send over your full contract and onboarding details.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                Please feel free to reach out with any questions — or use the buttons below to <strong>accept or decline the offer</strong> directly.
              </p>

              {/* Action Buttons */}
              {canTakeAction() ? (
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <button
                    onClick={handleOfferAccept}
                    disabled={actionLoading}
                    className="flex-1 bg-green-600 text-white px-6 py-4 rounded-lg font-semibold text-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {actionLoading ? (
                      <span className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Processing...
                      </span>
                    ) : (
                      '👍 Accept Offer'
                    )}
                  </button>
                  <button
                    onClick={handleOfferDecline}
                    disabled={actionLoading}
                    className="flex-1 bg-red-600 text-white px-6 py-4 rounded-lg font-semibold text-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {actionLoading ? (
                      <span className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Processing...
                      </span>
                    ) : (
                      '👎 Decline Offer'
                    )}
                  </button>
                </div>
              ) : (
                <div className="text-center py-6 mb-6">
                  <div className="text-6xl mb-4">
                    {offer.status === 'ACCEPTED' ? '✅' : 
                     offer.status === 'DECLINED' || offer.status === 'REJECTED' ? '❌' : 
                     isOfferExpired() ? '⏰' : '📋'}
                  </div>
                  <p className="text-lg font-medium text-gray-700">
                    {isOfferExpired() ? 'This offer has expired' : 
                     `Offer ${offer.status?.toLowerCase()}`}
                  </p>
                </div>
              )}
            </div>

            {/* Footer Message */}
            <div className="border-t border-gray-200 pt-6">
              <p className="text-gray-700 leading-relaxed mb-4">
                Thank you again for your time and interest in {offer.job?.company || offer.service?.company || 'our company'}. We're excited about the possibility of you joining the team!
              </p>
              
              <div className="mb-6">
                <p className="text-gray-700 mb-2"><strong>Warm regards,</strong></p>
                <p className="font-semibold text-gray-900">{getSenderName()}</p>
                {offer.sender?.email && (
                  <p className="text-gray-600">{offer.sender.email}</p>
                )}
                <p className="text-gray-600">{offer.job?.company || offer.service?.company || 'Company'}</p>
              </div>

              {/* AIAURA Branding */}
              <div className="text-center py-4 border-t border-gray-100">
                <p className="text-sm text-gray-500">
                  <em>Powered by AIAURA</em>
                </p>
              </div>

              {/* Technical Details */}
              <div className="text-xs text-gray-400 mt-4 pt-4 border-t border-gray-100">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p><strong>Date Created:</strong> {formatDate(offer.date_published)}</p>
                    <p><strong>Last Updated:</strong> {formatDate(offer.date_updated)}</p>
                  </div>
                  <div>
                    <p><strong>Offer ID:</strong> #{offer.id.split('-')[0]}</p>
                    <p><strong>Status:</strong> {offer.status}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Warning Messages */}
        {(offer.status === 'DRAFT' || offer.status === 'draft') && !isOfferExpired() && (
          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center gap-2 text-yellow-800">
              <span className="text-xl">⚠️</span>
              <p className="font-medium">
                This offer is awaiting your response. Please review the details and take action above.
              </p>
            </div>
          </div>
        )}

        {isOfferExpired() && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2 text-red-800">
              <span className="text-xl">⏰</span>
              <p className="font-medium">
                This offer has expired and can no longer be accepted or declined. Please contact the sender if you need to discuss this further.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OfferDetails;